import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { routeToEnrouteView, routeToTrolleyView } from "Helpers/index";
import moment from "moment";
import { Row, Col } from "antd";
import DataTableFilter from "Components/shared/data-table-filter";
import { DataTable } from "@life-line-lab/components-react";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";

interface EnrouteTrolleysListProps {
  trolleyList?: any;
  handoverOptions?: any;
  onFilter?: any;
}

const EnrouteTrolleysList: React.FC<EnrouteTrolleysListProps> = ({
  trolleyList,
  handoverOptions,
  onFilter,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const initialFilter = {
    trolleyStatus: "",
    handoverPoint: "",
    fromDate: "",
    toDate: moment(),
  };

  const [filterState, setFilterState] = useState<any>(initialFilter);

  const initialFilterSelection: any[] = [
    {
      type: "date",
      key: "fromDate",
      name: "fromDate",
      placeholder: "YYYY-MM-DD",
      label: t("label.from"),
      value: filterState.fromDate ? moment(filterState.fromDate) : undefined,
    },
    {
      type: "date",
      key: "toDate",
      name: "toDate",
      placeholder: "YYYY-MM-DD",
      label: t("label.to"),
      value: filterState.toDate ? moment(filterState.toDate) : moment(),
    },
    {
      key: "trolleyStatus",
      name: "trolleyStatus",
      placeholder: t("placeholder.byStatus"),
      label: t("placeholder.byStatus"),
      value: filterState?.trolleyStatus,
      options: [
        { label: t("option.all"), value: "" },
        { label: "Boarded", value: "BOARDED" },
        { label: "Dispatched", value: "DISPATCHED" },
        { label: "Unloaded", value: "UNLOADED" },
        { label: "Received", value: "RECEIVED" },
        { label: "Closed", value: "CLOSED" },
      ],
    },
    {
      key: "handoverPoint",
      name: "handoverPoint",
      placeholder: t("label.handoverPoint"),
      label: t("label.handoverPoint"),
      value: filterState?.handoverPoint,
      options: [{ label: t("option.all"), value: "" }].concat(
        handoverOptions?.map((place) => {
          return {
            label: place.name,
            value: place.id,
          };
        }),
      ),
    },
  ];

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  // search by handoverDataPoints
  const handleFilterSelection = (selection: any) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      fromDate: selection.fromDate,
      toDate: selection.toDate,
      trolleyStatus: selection.trolleyStatus,
      handoverPoint: selection.handoverPoint,
    }));
  };

  const handleOpenCompanyEnrouteView = (enrouteID) => {
    if (enrouteID) routeToEnrouteView(enrouteID, location, history);
  };

  const handleOpenTrolleyView = (trolleyID) => {
    // not sure what this params is for
    const params = "redirect=enroute";
    routeToTrolleyView(trolleyID, location, history, params);
  };

  const columns: any = [
    {
      title: t("label.date").toLocaleUpperCase(),
      dataIndex: "createdAt",
      width: 70,
      ellipsis: true,
      sorter: (a, b) =>
        a.createdAt > b.createdAt ? 1 : a.createdAt < b.createdAt ? -1 : 0,
      defaultSortOrder: "descend",
      render: (createdAt: string) => {
        return createdAt ? (
          <span>
            {moment(createdAt).format("DD-MM-YYYY") +
              " | " +
              moment(createdAt).format("hh:mm a")}
          </span>
        ) : (
          <span>{"-"}</span>
        );
      },
    },
    {
      title: t("label.journeyNumber").toLocaleUpperCase(),
      dataIndex: ["trainPlanData", "journeyCode"],
      width: 70,
      ellipsis: true,
      render: (journeyCode: string, record: any) => {
        return record.trainPlanID ? (
          <a className="clickable-id">{journeyCode}</a>
        ) : (
          <span>{t("txt.notInJourney")}</span>
        );
      },
      onCell: (record: any) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenCompanyEnrouteView(record.trainPlanID);
          },
        };
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      width: 100,
      ellipsis: true,
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {toCapitalisedSpaced(text)}
          </span>
        );
      },
    },
    {
      title: t("label.trolleyName").toLocaleUpperCase(),
      dataIndex: ["trolleyCode"],
      width: 100,
      ellipsis: true,
      render: (trolleyCode: any) => {
        return <a className="clickable-id">{trolleyCode}</a>;
      },
      onCell: (record: any) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenTrolleyView(record._id);
          },
        };
      },
    },
    {
      title: t("label.noOfBoxes").toLocaleUpperCase(),
      dataIndex: "boxParcelListData",
      width: 100,
      ellipsis: true,
      render: (boxParcelListData: any) => {
        return <span>{boxParcelListData?.length}</span>;
      },
    },
    {
      title: t("label.handovers").toLocaleUpperCase(),
      dataIndex: ["handoverPointData", "name"],
      width: 100,
      ellipsis: true,
    },
  ];

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(
        filterState,
        (input) => isNil(input) || isEmpty(input),
      );
      onFilter(filterPayload);
    }
  }, [filterState]);

  return (
    <ErrorBoundary>
      <div className="parcel-order-list status-selector">
        <DataTable
          rowKey="_id"
          columns={columns}
          scroll={{ x: true }}
          dataSource={trolleyList}
          title={() => (
            <Row>
              <Col span={16} className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.enrouteTrolleySearchbar")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={initialFilterSelection}
                />
              </Col>
            </Row>
          )}
        />
      </div>
    </ErrorBoundary>
  );
};

export default EnrouteTrolleysList;
